"use client"

import React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import HTMLRenderer from "@/components/ui/html-renderer"

export type WhyItem = {
  icon: React.ElementType
  title: string
  description: string
  badge?: string
}

interface WhyChooseUsCardsProps {
  items: WhyItem[]
  variant?: "default" | "simple"
  className?: string
  cardClassName?: string
  gridClassName?: string
}

/**
 * Reusable grid of "¿Por qué elegirnos?" cards.
 * Shows a primary-colored top line on hover.
 */
export default function WhyChooseUsCards({
  items,
  variant = "default",
  className,
  cardClassName,
  gridClassName,
}: WhyChooseUsCardsProps) {
  const gridClasses = gridClassName ?? "grid md:grid-cols-2 gap-12 max-w-5xl mx-auto"
  return (
    <div className={cn(gridClasses, className)}>
      {items.map((it, index) => {
        const Icon = it.icon
        const isSimple = variant === "simple"
        return (
          <Card
            key={index}
            className={cn(
              "group relative overflow-hidden bg-white shadow-md border border-gray-100 rounded-xl",
              // allow page to customize more
              cardClassName
            )}
            style={{ animationDelay: `${index * 0.12}s` }}
          >
            {/* top accent (Nosotros style) - scale on hover */}
            <div className="absolute top-0 left-0 h-1 w-full bg-[#FB0009] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200" />
            <CardContent className={cn("text-center p-6", isSimple && "p-5")}>
              {/* Icon block */}
              {isSimple ? (
                <div className="h-16 w-16 rounded-full bg-[#fb0009] flex items-center justify-center mx-auto mb-4 text-white">
                  <Icon className="h-7 w-7 text-white" />
                </div>
              ) : (
                <div className="w-20 h-20 bg-white border border-[#fb0009] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <Icon className="h-10 w-10 text-[#fb0009]" />
                </div>
              )}

              {/* Text */}
              <h3
                className={cn(
                  "font-bold text-black mb-4",
                  isSimple ? "text-lg" : "text-2xl"
                )}
                style={{ fontFamily: 'DIN, sans-serif' }}
              >
                {it.title}
              </h3>
              <div className={cn("text-black leading-relaxed", isSimple ? "" : "text-lg mb-6")}>
                <HTMLRenderer content={it.description} />
              </div>

              {/* Badge row (only for default variant and when badge provided) */}
              {!isSimple && it.badge && (
                <div className="flex items-center justify-center space-x-3 text-primary">
                  <div className="w-2 h-2 bg-[#FB0009] rounded-full" />
                  <span className="font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>{it.badge}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
